import React, {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ChevronDown, MinusSquare, PlusSquare } from "react-feather";

const useAnimationFrame = (callback) => {
  const requestRef = useRef();
  const previousTimeRef = useRef();

  const animate = useCallback(
    (time) => {
      if (previousTimeRef.current !== undefined) {
        const deltaTime = time - previousTimeRef.current;
        callback(deltaTime);
      }
      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    },
    [callback],
  );

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [animate]);
};

export function AccordionItem({
  children,
  title,
  subtitle,
  icon,
  iconright,
  titleright,
  defaultActive = false,
  iconWidth = 21,
  background = "",
  style = "block w-full h-full relative m-0",
  className = "text-typo shadow-small",
  marginStyle = "mb-2",
  titleStyle = "text-lg",
  chevron = true,
  tags = [],
  onResizeTick = null,
  onToggle = (_) => null,
}) {
  const [isActive, setIsActive] = useState(defaultActive);
  const [titleState, setTitleState] = useState(title);
  const [subtitleState, setSubtitleState] = useState(subtitle);
  const [iconState, setIcon] = useState(icon);
  const [iconWidthState, setIconWidth] = useState(iconWidth);
  const [iconRightState, setIconRight] = useState(iconright);

  useEffect(() => {
    if (titleState !== title) {
      setTitleState(title);
    }
  }, [title]);

  function AccordionTitle() {
    if (!titleState) {
      return null;
    }
    return <span className={titleStyle}>{titleState}</span>;
  }

  function AccordionSubtitle() {
    if (!subtitleState) {
      return null;
    }
    return (
      <span className="ml-3 inline-flex flex-wrap items-center text-md">
        {subtitleState}
      </span>
    );
  }

  function AccordionIcon() {
    if (!iconState) {
      return null;
    }
    return React.isValidElement(iconState) ? (
      iconState
    ) : (
      <img
        className="-mt-1 mr-2 inline"
        src={`/images/${iconState}.svg`}
        width={`${iconWidthState}px`}
      />
    );
  }

  function AccordionIconRight() {
    if (!iconRightState) {
      return null;
    }
    return React.isValidElement(iconRightState) ? (
      iconRightState
    ) : (
      <img
        className="-mt-1 mr-2 inline"
        src={`/images/${iconRightState}.svg`}
        width={`${iconWidthState}px`}
      />
    );
  }

  function setAccordionHeading(value, updateTitle = true) {
    if (updateTitle) {
      setTitleState(value);
    } else {
      setSubtitleState(value);
    }
  }

  function setAccordionIcon(icon, width = 21) {
    setIcon(icon);

    if (iconWidthState !== width) {
      setIconWidth(width);
    }
  }

  function setAccordionRight(icon, width = 21) {
    setIconRight(icon);

    if (iconWidthState !== width) {
      setIconWidth(width);
    }
  }

  const [isAnimating, setIsAnimating] = useState(false);
  const animationStepRef = useRef(0);

  const handleAnimation = useCallback(
    (deltaTime) => {
      const animationTime = 150;
      const frames = 30;
      const stepSize = 1 / frames;
      const stepTime = animationTime / frames;

      animationStepRef.current += stepSize * (deltaTime / stepTime);
      onResizeTick(isActive);

      if (animationStepRef.current >= 1) {
        setIsAnimating(false);
      }
    },
    [isActive, onResizeTick],
  );

  useAnimationFrame((deltaTime) => {
    if (isAnimating) {
      handleAnimation(deltaTime);
    }
  });

  const startAnimating = () => {
    if (onResizeTick !== null && !isAnimating) {
      animationStepRef.current = 0;
      setIsAnimating(true);
    }
  };

  const toggleAccordion = () => {
    const nextState = !isActive;
    setIsActive(nextState);

    onToggle(nextState);
    startAnimating();
  };

  const childrenWithProps = Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, {
        setAccordionHeading,
        setAccordionIcon,
        defaultActive,
        setAccordionRight,
        setIsActive,
        isActive,
        startAnimating,
      });
    }
    return child;
  });

  return (
    <div
      className={`${background} ${className} ${style} ${isActive ? "open" : ""} ${marginStyle} transition-all`}
    >
      <header
        className={`relative flex select-none justify-between p-4 hover:cursor-pointer`}
        onClick={() => toggleAccordion()}
        onKeyUp={(e) => e.key === "Enter" && toggleAccordion()}
        role="button"
        tabIndex="0"
        aria-expanded={isActive}
      >
        <div className="flex items-center">
          <AccordionIcon />
          <AccordionTitle />
          <AccordionSubtitle />
        </div>
        <div className="flex items-center">
          <AccordionIconRight />
          {tags.map((tag, index) => (
            <span
              key={index}
              className="mr-1 inline-flex items-center rounded-sm bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-typo"
            >
              {tag}
            </span>
          ))}
          <span className="mx-2">{titleright}</span>
          {chevron && (
            <ChevronDown
              className={`transform transition-all ${isActive ? "rotate-180" : ""}`}
            />
          )}
          {!chevron && (
            <>
              {isActive && <MinusSquare stroke="#363636" />}
              {!isActive && <PlusSquare stroke="#363636" />}
            </>
          )}
        </div>
      </header>
      {isActive && (
        <div className="children overflow-x-auto p-4 pt-2">
          {childrenWithProps}
        </div>
      )}
    </div>
  );
}
